import type { Migration, MigrationRun } from './api/migrationsClient';

type WithRuns = Pick<Migration, 'lastApply' | 'lastDryRun'>;

/** Who started a run: their email, which every run records. */
export const runnerName = (run: MigrationRun) => run.startedBy?.email || 'Unknown';

/** The newer of the last real run and the last dry run. */
export const lastRunOf = (m: WithRuns): MigrationRun | null => {
  const runs = [m.lastApply, m.lastDryRun].filter((r): r is MigrationRun => !!r);
  if (runs.length === 0) return null;
  return runs.reduce((a, b) => ((a.startedAt ?? '') >= (b.startedAt ?? '') ? a : b));
};

type Named = Pick<Migration, 'id' | 'ticket'>;

/** The ticket number from `ticket` ("SF-5093"), else from an id that starts "SF-5093-". */
export const ticketNumber = (m: Named): number | null => {
  const match = /^SF-(\d+)$/.exec(m.ticket ?? '') ?? /^SF-(\d+)-/.exec(m.id);
  return match ? Number(match[1]) : null;
};

const byId = (a: Named, b: Named) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/**
 * Newest first. Ticket numbers only go up over time, so a higher number is a newer migration.
 * Steps of one ticket keep id order (so "SF-5088-move-..." stays before "SF-5088-shrink-...");
 * migrations with no ticket number go last, by id.
 */
export const newestFirst = <T extends Named>(list: readonly T[]): T[] =>
  [...list].sort((a, b) => {
    const na = ticketNumber(a);
    const nb = ticketNumber(b);
    if (na !== nb) {
      if (na === null) return 1;
      if (nb === null) return -1;
      return nb - na;
    }
    return byId(a, b);
  });
